import { Injectable, signal, computed } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { GeoJSONSource } from 'mapbox-gl';
import _ from 'lodash';
import {
  BehaviorSubject,
  fromEvent,
  merge,
  Observable,
  of,
  Subject,
  takeUntil,
  throwError
} from 'rxjs';
import { MapConstants } from '../constants/ioh-map.constants';
import { LngLat, LngLatBounds } from 'mapbox-gl';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  determineTrainColors,
  determineTrainIcon,
  determineTrainLightIcon
} from '../shared/utils/iohMapTrainUtils';
import { IOHDynamicDialogService } from '../shared/service/ioh-dialog.service';

interface GeoJSON {
  type: string;
  features?: any[];
  geometry?: any;
  coordinates?: number[][];
}

type Position = number[];

@Injectable({
  providedIn: 'root'
})
export class IOHMapService {
  trainsForMap: any;
  terminalTrainsForMap: any;
  terminalsForMap: any;
  map: mapboxgl.Map;
  private removeLabelEvents$;
  private navControl: mapboxgl.NavigationControl;
  private geolocateControl: mapboxgl.GeolocateControl;
  private attributionControl: mapboxgl.AttributionControl;
  private mapControlsVisible = true;
  private networkBounds = new mapboxgl.LngLatBounds([-124.3, 18.09], [-56.93, 52.15]);

  /*
  mapbox popup
  */
  static popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '600px'
  });
  /*
  Mapbox marker
  */
  marker$ = new mapboxgl.Marker({
    color: '#008080',
    scale: 1,
    draggable: false,
    pitchAlignment: 'auto',
    rotationAlignment: 'auto'
  });

  zoomIohValue: number = MapConstants.DEFAULT_ZOOM;
  public iohZoomLevel = signal<number>(4);
  locationData: any;
  longitude: number;
  latitude: number;
  public iohHideBaseLayer$ = new BehaviorSubject(false);
  iohTrainLengthByCount: number[][] = Array.from({ length: 5 }, (_, index) =>
    Array(index).fill(0)
  );
  iohMapType: string = 'Dark';
  private level3MapType: string = 'Dark';

  trainLabelThresholdCount = `${environment.appConfig.trainLabelThresholdCount}`;
  _isIohFilterPatched = new BehaviorSubject<boolean>(false);
  isIohFilterPatched$ = this._isIohFilterPatched.asObservable();
  private destroy$ = new Subject<void>();

  currentLevel = 0;
  iohShowTrainLabels$ = new BehaviorSubject<boolean>(false);

  private mapSlotElements: Partial<Record<'level1' | 'level3', HTMLElement>> = {};
  private mapSlotResizeObserver = new ResizeObserver(() => this.refreshMapSlotRect());
  private mapHostElement: HTMLElement | null = null;
  activeMapSlot = signal<'level1' | 'level3' | null>(null);
  isLevel1Active = computed(() => this.activeMapSlot() === 'level1');
  isLevel3Active = computed(() => this.activeMapSlot() === 'level3');
  mapStyleReady = signal<boolean>(false);
  mapViewSettled = signal<boolean>(true);

  markStyleReloading(): void {
    this.mapStyleReady.set(false);
  }

  constructor(
    private http: HttpClient,
    private dynamicDialogService: IOHDynamicDialogService
  ) {
    this.displayIohL3Data = _.debounce(this.displayIohL3Data.bind(this), 300);
    merge(this.iohShowTrainLabels$).subscribe(data => {
      this.displayIohL3Data();
    });
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshMapSlotRect());
  }

  registerMapSlot(id: 'level1' | 'level3', el: HTMLElement | null) {
    const existing = this.mapSlotElements[id];
    if (existing) this.mapSlotResizeObserver.unobserve(existing);
    if (el) {
      this.mapSlotElements[id] = el;
      this.mapSlotResizeObserver.observe(el);
    } else {
      delete this.mapSlotElements[id];
    }
    this.attachMapToActiveSlot();
    this.refreshMapSlotRect();
  }

  // registers the map's real host element (see mapHostElement above) - called once from ioh-home.component.
  registerMapHost(el: HTMLElement | null): void {
    if (el) {
      document.querySelectorAll('[data-ioh-map-host]').forEach(stray => {
        if (stray !== el) {
          stray.remove();
        }
      });
    }
    this.mapHostElement = el;
    this.attachMapToActiveSlot();
  }

  private attachMapToActiveSlot(): void {
    const id = this.activeMapSlot();
    const slotEl = id ? this.mapSlotElements[id] : null;
    if (this.mapHostElement && slotEl && this.mapHostElement.parentElement !== slotEl) {
      slotEl.appendChild(this.mapHostElement);
      requestAnimationFrame(() => this.map?.resize());
    }
  }

  setActiveMapSlot(id: 'level1' | 'level3' | null) {
    const previousSlot = this.activeMapSlot();
    this.activeMapSlot.set(id);
    if (previousSlot !== id) {
      if (id === 'level1') {
        this.applyMapStyle('Dark');
      } else if (id === 'level3') {
        this.applyMapStyle(this.level3MapType);
      }
    }
    this.mapViewSettled.set(false);
    const isLevel1 = id === 'level1';
    this.setTrainsLayerVisible(!isLevel1);
    this.setMapControlsVisible(!isLevel1);
    this.setMapDraggingEnabled(!isLevel1);
    this.setMapZoomInteractionsEnabled(!isLevel1);
    this.setCsxTrackLayersVisible(!isLevel1);
    this.setTerminalsLayerVisible(isLevel1);
    this.attachMapToActiveSlot();
    this.applyMapViewForSlot(id);
  }

  setMapStyle(style: string): void {
    if (this.activeMapSlot() !== 'level1') {
      this.level3MapType = style;
    }
    this.applyMapStyle(style);
  }

  private applyMapStyle(style: string): void {
    const styleUrl =
      style === 'Mono'
        ? environment.appConfig.map.mapintermodalStyleMono
        : style === 'Roads'
          ? environment.appConfig.map.mapintermodalStyleRoad
          : environment.appConfig.map.mapintermodalStyleDark;
    if (this.iohMapType === style) {
      return;
    }
    this.markStyleReloading();
    this.iohMapType = style;
    this.map?.setStyle(styleUrl);
  }

  fitLevel1ToTerminals(): void {
    if (!this.map || !this.isLevel1Active()) {
      return;
    }
    const coords = (this.lastTerminalsRaw || [])
      .filter(terminal => terminal?.latitude && terminal?.longitude)
      .map(terminal => [Number(terminal.longitude), Number(terminal.latitude)] as [number, number]);
    if (!coords.length) {
      this.map.jumpTo({
        center: [MapConstants.LONGITUDE, MapConstants.LATITUDE],
        zoom: MapConstants.DEFAULT_ZOOM
      });
      return;
    }
    const bounds = coords.reduce(
      (b, coord) => b.extend(coord),
      new mapboxgl.LngLatBounds(coords[0], coords[0])
    );
    const mapContainer = this.map.getContainer();
    const edgePadding = Math.max(
      32,
      Math.min(80, Math.round(Math.min(mapContainer.clientWidth, mapContainer.clientHeight) * 0.12))
    );
    this.map.fitBounds(bounds, {
      padding: edgePadding,
      maxZoom: 5,
      duration: 0
    });
  }

  private appliedSlotId: 'level1' | 'level3' | null | undefined = undefined;
  private appliedSlotSize: { width: number; height: number } | null = null;
  private applyMapViewRetryCount = 0;

  private applyMapViewForSlot(id: 'level1' | 'level3' | null) {
    const container = this.map?.getContainer();
    const slotEl = id ? this.mapSlotElements[id] : null;
    const targetSize = slotEl ? { width: slotEl.clientWidth, height: slotEl.clientHeight } : null;
    const sizeMatchesTarget =
      !!targetSize &&
      Math.abs((container?.offsetWidth ?? 0) - targetSize.width) < 2 &&
      Math.abs((container?.offsetHeight ?? 0) - targetSize.height) < 2;
    const ready = !!this.map && sizeMatchesTarget && this.mapStyleReady();
    if (!ready) {
      if (this.map && targetSize && !sizeMatchesTarget) {
        this.map.resize();
      }
      if (this.applyMapViewRetryCount < 30) {
        this.applyMapViewRetryCount++;
        setTimeout(() => this.applyMapViewForSlot(this.activeMapSlot()), 150);
      }
      return;
    }
    this.applyMapViewRetryCount = 0;
    const sizeUnchanged =
      !!this.appliedSlotSize &&
      !!targetSize &&
      Math.abs(this.appliedSlotSize.width - targetSize.width) < 2 &&
      Math.abs(this.appliedSlotSize.height - targetSize.height) < 2;
    if (this.appliedSlotId === id && sizeUnchanged) {
      this.mapViewSettled.set(true);
      return;
    }
    this.appliedSlotId = id;
    this.appliedSlotSize = targetSize;
    this.map.resize();
    if (id === 'level1') {
      this.map.setMinZoom(1);
      this.map.setMaxBounds(null);
      this.fitLevel1ToTerminals();
    } else if (id === 'level3') {
      this.map.setMinZoom(MapConstants.DEFAULT_ZOOM);
      this.map.setMaxBounds(this.networkBounds);
      this.map.jumpTo({
        center: [MapConstants.LEVEL3_LONGITUDE, MapConstants.LEVEL3_LATITUDE],
        zoom: MapConstants.DEFAULT_ZOOM
      });
    }
    this.mapViewSettled.set(true);
  }

  setMapControlsVisible(visible: boolean) {
    if (!this.map || visible === this.mapControlsVisible) return;
    this.mapControlsVisible = visible;
    if (visible) {
      this.map.addControl(this.navControl, 'top-right');
      this.map.addControl(this.geolocateControl, 'top-right');
      this.map.addControl(this.attributionControl, 'bottom-right');
    } else {
      this.map.removeControl(this.navControl);
      this.map.removeControl(this.geolocateControl);
      this.map.removeControl(this.attributionControl);
    }
  }

  private setMapDraggingEnabled(enabled: boolean) {
    if (!this.map) return;
    enabled ? this.map.dragPan.enable() : this.map.dragPan.disable();
  }

  private setMapZoomInteractionsEnabled(enabled: boolean) {
    if (!this.map) return;
    const handlers = [
      this.map.scrollZoom,
      this.map.boxZoom,
      this.map.doubleClickZoom,
      this.map.touchZoomRotate,
      this.map.keyboard
    ];
    handlers.forEach(handler => (enabled ? handler.enable() : handler.disable()));
  }

  private trainLayerIds = [
    MapConstants.INTERMODALMAP_TRAIN_LAYER.id,
    MapConstants.INTERMODALMAP_TRAIN_LABEL__DARK_LAYER.id,
    MapConstants.INTERMODALMAP_TRAIN_LABEL__LIGHT_LAYER.id
  ];

  setTrainsLayerVisible(visible: boolean) {
    this.trainLayerIds.forEach(id => {
      if (this.map?.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  }

  private terminalLayerIds = [
    MapConstants.INTERMODALMAP_TERMINALS_HALO_ID,
    MapConstants.INTERMODALMAP_TERMINALS_DOT_ID,
    MapConstants.INTERMODALMAP_TERMINALS_LABEL_ID
  ];

  setTerminalsLayerVisible(visible: boolean) {
    this.terminalLayerIds.forEach(id => {
      if (this.map?.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  }


  setCsxTrackLayersVisible(visible: boolean) {
    MapConstants.CSX_TRACK_LAYERS.forEach(id => {
      if (this.map?.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  }

  private lastTerminalsRaw: any[] = [];
  private lastGateInfo: any = {};

  private getTerminalDotColor(terminal: any, gateInfo: any): string {
    const avgTurnTimeMinutes = gateInfo?.terminals?.[terminal.code]?.avgTurnTimeMinutes;
    return avgTurnTimeMinutes > environment.appConfig.iohGateAvgTurnTimeMints
      ? MapConstants.TERMINAL_DOT_COLOR_BAD
      : MapConstants.TERMINAL_DOT_COLOR_GOOD;
  }

  iohShowTerminalsOnMap(terminals: any[], gateInfo: any = this.lastGateInfo): void {
    this.lastTerminalsRaw = terminals || [];
    this.lastGateInfo = gateInfo || {};
    if (!this.map || !this.mapStyleReady()) {
      return;
    }
    const features = (terminals || [])
      .filter(terminal => terminal?.latitude && terminal?.longitude)
      .map(terminal => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(terminal.longitude), Number(terminal.latitude)]
        },
        properties: {
          name: terminal.name,
          code: terminal.code,
          terminalCode: terminal.code || terminal.terminalCode,
          avgTurnTimeMinutes: this.lastGateInfo?.terminals?.[terminal.code]?.avgTurnTimeMinutes ?? null,
          dotColor: this.getTerminalDotColor(terminal, this.lastGateInfo)
        }
      }));
    this.terminalsForMap = { type: 'FeatureCollection', features };
    this.iohAddOrUpdateSourceAndLayer(
      this.terminalsForMap,
      MapConstants.INTERMODALMAP_TERMINALS_SOURCE_ID,
      [
        MapConstants.INTERMODALMAP_TERMINALS_HALO_LAYER,
        MapConstants.INTERMODALMAP_TERMINALS_DOT_LAYER,
        MapConstants.INTERMODALMAP_TERMINALS_LABEL_LAYER
      ]
    );
    this.setTerminalsLayerVisible(this.isLevel1Active());
    if (features.length > 0) {
      this.bindTerminalClickEvents();
      this.fitLevel1ToTerminals();
    }
  }

  private terminalClickBound = false;

  private bindTerminalClickEvents(): void {
    if (this.terminalClickBound || !this.map) {
      return;
    }
    this.terminalClickBound = true;
    this.terminalLayerIds.forEach(layerId => {
      this.map.on('mouseenter', layerId, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', layerId, () => {
        this.map.getCanvas().style.cursor = '';
      });
    });
    this.map.on('click', MapConstants.INTERMODALMAP_TERMINALS_HALO_ID, event => {
      const props: any = event.features?.[0]?.properties;
      if (props?.code && !props.name?.includes('JUNCTION')) {
        this.dynamicDialogService.showDialog(props.code);
      }
    });
  }

  refreshMapSlotRect() {
    this.applyMapViewForSlot(this.activeMapSlot());
    requestAnimationFrame(() => {
      this.map?.resize();
    });
  }

  displayIohL3Data(): void {
    this.iohShowTrainsOnMap();
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = JSON.stringify(error);
    return throwError(() => new Error(`Error: ${errorMessage}`));
  }

  set isIohFilterPatched(value: boolean) {
    this._isIohFilterPatched.next(value);
  }

  get isIohFilterPatched(): boolean {
    return this._isIohFilterPatched.value;
  }

  get showIohTrainLabels(): boolean {
    return this.iohShowTrainLabels$.value;
  }

  set showIohTrainLabels(value: boolean) {
    this.iohShowTrainLabels$.next(value);
  }

  iohFormatTrainName(trainId: string): {
    firstPart: string;
    middlePart: string;
    lastPart: string;
  } {
    if (trainId.length !== 6) {
      throw new Error('Invalid trainName format. Expected 6 characters.');
    }

    const firstPart = trainId.slice(0, 1);
    const middlePart = trainId.slice(1, 4);
    const lastPart = trainId.slice(4);

    return {
      firstPart,
      middlePart,
      lastPart
    };
  }

  iohStatusColor(status: string) {
    let color;
    switch (status) {
      case 'Late':
        color = '#e33a31';
        break;
      case 'Early':
        color = '#2F6EF7';
        break;
      case 'Delayed':
        color = '#ffbd33';
        break;
      case 'AOT':
        color = '#15c154';
        break;
      default:
        color = 'black';
    }
    return color;
  }

  iohShowTrainsOnMap() {
    if (!this.map) {
      console.error('Map is not initialized Yet.');
      return;
    }

    const { trainsForMap, showIohTrainLabels, isIohFilterPatched, iohMapType } =
      this;

    const features = trainsForMap?.features || [];
    features.forEach(feature => {
      const { properties: props } = feature;

      feature.properties = {
        ...props,
        ...determineTrainColors(props),
        iconImage: determineTrainIcon(props),
        iconLightImage: determineTrainLightIcon(props),
        customUrl: isIohFilterPatched,
        totalFeatureCount: features.length,
        showLabels: showIohTrainLabels
      };
    });
    this.hideIohSpecificLayers();
    const labelLayer =
      iohMapType === 'Dark'
        ? MapConstants.INTERMODALMAP_TRAIN_LABEL__DARK_LAYER
        : MapConstants.INTERMODALMAP_TRAIN_LABEL__LIGHT_LAYER;
    if (features.length > 0) {
      this.iohAddOrUpdateSourceAndLayer(
        trainsForMap,
        MapConstants.INTERMODALMAP_LS_ID,
        [MapConstants.INTERMODALMAP_TRAIN_LAYER, labelLayer]
      );
      this.setTrainsLayerVisible(this.activeMapSlot() !== 'level1');
    }
  }

  InitalizeIohMap(wrapperEl?: HTMLElement): Observable<void> {
    if (this.map && wrapperEl && wrapperEl.isConnected) {
      const existingContainer = this.map.getContainer(); 
      const placeholder = wrapperEl.querySelector<HTMLElement>('#IOHMap');
       if (placeholder) {
         placeholder.replaceWith(existingContainer);
       } else {
        wrapperEl.appendChild(existingContainer);
      }
      this.appliedSlotId = undefined;
      this.appliedSlotSize = null;
      this.applyMapViewRetryCount = 0;
      requestAnimationFrame(() => {
        this.map?.resize();
        this.setActiveMapSlot(this.activeMapSlot());
      });
      return of(void 0);
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.mapStyleReady.set(false);
    this.appliedSlotSize = null;
    this.applyMapViewRetryCount = 0;
    this.map = new mapboxgl.Map({
      container: wrapperEl?.querySelector<HTMLElement>('#IOHMap') ?? 'IOHMap',
      accessToken: environment.appConfig.map.mapboxintermodalToken,
      style: environment.appConfig.map.mapintermodalStyleDark,
      center: [MapConstants.LONGITUDE, MapConstants.LATITUDE],
      zoom: MapConstants.DEFAULT_ZOOM,
      minZoom: MapConstants.DEFAULT_ZOOM,
      maxZoom: 20,
      maxBounds: this.networkBounds,
      attributionControl: false,
      projection: 'mercator'
    });
    //this.setMapZoomInteractionsEnabled(this.activeMapSlot() !== 'level1');
    this.setMapDraggingEnabled(this.activeMapSlot() !== 'level1');
    window['iohMapInstance'] = this.map;
    this.loadIohMap();
    this.displayIohTiltSpin();

    this.navControl = new mapboxgl.NavigationControl({ visualizePitch: true });
    this.geolocateControl = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true
    });
    this.attributionControl = new mapboxgl.AttributionControl();
    this.mapControlsVisible = true;
    this.map.addControl(this.navControl, 'top-right');
    this.map.addControl(this.geolocateControl, 'top-right');
    this.map.addControl(this.attributionControl, 'bottom-right');
    return new Observable<void>(observer => {
      this.map.on('style.load', () => {
        this.mapStyleReady.set(true);
        this.loadIohMap();
        this.displayIohTiltSpin();
        this.setActiveMapSlot(this.activeMapSlot());
        this.iohShowTerminalsOnMap(this.lastTerminalsRaw);
        observer.next();
        observer.complete();
      });
    });
  }

  displayIohTiltSpin() {
    this.map.setMaxPitch(0);
    this.map.setMinPitch(0);
    this.map.touchZoomRotate.disableRotation();
  }

  loadIohMap() {
    this.map.on('load', () => {
      this.map.on('zoom', () => {
        const zoomIohValue = this.map.getZoom();
        this.zoomIohValue = Math.round(zoomIohValue);
        this.iohZoomLevel.set(this.zoomIohValue);
      });
      this.initIohMouseEvents();
      this.addIohTrainsPopUp('click', MapConstants.INTERMODALMAP_LS_ID);
      this.addIohTrainsPopUp('click', MapConstants.INTERMODALMAP_LABEL_ID);
    });
  }

  addIohTrainsPopUp(eventType: string, source: string) {
    if (source === 'html-label-layer') {
      setTimeout(() => {
        const labelElements = document.querySelectorAll('.custom-label');
        if (this.removeLabelEvents$) {
          this.removeLabelEvents$.next();
          this.removeLabelEvents$.complete();
        }
        this.removeLabelEvents$ = new Subject<void>();
        labelElements.forEach(labelElement => {
          fromEvent(labelElement, 'click')
            .pipe(takeUntil(this.removeLabelEvents$))
            .pipe(takeUntil(this.destroy$))
            .subscribe(_event => {
              this.map.getCanvas().style.cursor = 'auto';
              const featureIndex = labelElement.classList[1].split('-')[1];
              const feature = this.trainsForMap.features[featureIndex];
              this.dynamicDialogService.showTrainCallout(feature.properties);
            });
        });
      }, 0);
    } else {
      this.map.on(eventType, source, event => {
        this.map.getCanvas().style.cursor = 'auto';
        const eventCopy = { ...(event as object) };
        const feature = (eventCopy as { features?: any[] })?.features?.[0];
        this.dynamicDialogService.showTrainCallout(feature.properties);
      });
    }
  }

  initIohMouseEvents() {
    this.map.on('mouseenter', MapConstants.INTERMODALMAP_LS_ID, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', MapConstants.INTERMODALMAP_LS_ID, () => {
      this.map.getCanvas().style.cursor = '';
    });
    this.map.on('mouseenter', MapConstants.INTERMODALMAP_LABEL_ID, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', MapConstants.INTERMODALMAP_LABEL_ID, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  setIohVisibility(layerId: string, _display: string) {
    if (this.map?.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', 'none');
    }
  }
  hideIohLayers(layers: string[]) {
    layers.forEach(layer => {
      this.setIohVisibility(layer, 'none');
    });
  }
  hideIohSpecificLayers() {
    this.hideIohLayers(MapConstants.DISPLAY_NONE_OP);
  }

  iohZoomToAssetId(long, lat, zoom: number) {
    this.map?.flyTo({
      center: [long, lat],
      zoom: zoom,
      duration: 1000,
      essential: true
    });
  }

  public boundingBox(data): void {
    const sw = new LngLat(data.bbox[0], data.bbox[1]);
    const ne = new LngLat(data.bbox[2], data.bbox[3]);
    this.iohZoomTo(sw, ne);
    this.iohZoomTo(
      new LngLat(data.bbox[0], data.bbox[1]),
      new LngLat(data.bbox[2], data.bbox[3])
    );
  }
  public iohZoomTo(sw: LngLat, ne: LngLat, padding?: number): void {
    const rightPad = padding ? padding : 250;
    const leftPad = rightPad ? rightPad : rightPad + 200;
    this.map.fitBounds(new LngLatBounds(sw, ne), {
      padding: { top: 75, bottom: 75, left: leftPad, right: rightPad },
      linear: true
    });
  }

  public iohZoomToBoundingBox(coordinates, _padding?: number) {
    const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
    for (const coord of coordinates) {
      bounds.extend(coord);
    }
    this.map.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 }
    });
  }

  public zoomToSpecificBoundingBox(coordinates) {
    const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
    for (const coord of coordinates) {
      bounds.extend(coord);
    }
    this.map.fitBounds(bounds, {
      padding: { top: 0, bottom: 0, left: 0, right: 0 }
    });
  }

  public iohAddOrUpdateSourceAndLayer(
    data: any,
    sourceId: string,
    layers: any[]
  ) {
    if (this.map) {
      if (!this.map.getSource(sourceId)) {
        this.iohAddSourceAndLayer(sourceId, data, layers);
      }
      const source = this.map.getSource(sourceId) as GeoJSONSource;
      if (source && source.setData) {
        source.setData(data);
      } else {
        console.error(
          'Source is not a GeoJSONSource or does not support setData'
        );
      }
    }
  }

  public iohAddSourceAndLayer(sourceId: string, data: any, layers: any[]) {
    if (this.map) {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: data
      });
      layers.forEach(layer => {
        this.map.addLayer(layer);
      });
    }
  }

  public iohRemoveLayerAndSource(layerid: string) {
    if (this.map?.getLayer(layerid)) {
      this.map.removeLayer(layerid);
    }
    if (this.map?.getSource(layerid)) {
      try {
        this.map.removeSource(layerid);
      } catch (e) {
        console.log(' removeSource failed ', e);
      }
    }
  }

  removeIohLayers(layers: string[]) {
    layers.forEach(layer => {
      this.iohRemoveLayerAndSource(layer);
    });
  }

  resetIohMap() {
    this.map.jumpTo({
      center: [MapConstants.LEVEL3_LONGITUDE, MapConstants.LEVEL3_LATITUDE],
      zoom: MapConstants.DEFAULT_ZOOM
    });
    this.map.resetNorth();
  }

  public resetIohMapService(): void {
    try {
      if (this.map) {
        this.resetIohMap();
      }
      this.showIohTrainLabels = false;
      this.zoomIohValue = MapConstants.DEFAULT_ZOOM;
      this.iohZoomLevel.set(Math.round(MapConstants.DEFAULT_ZOOM));
      this.iohMapType = 'Dark';
    } catch (error) {
      console.error('Error resetting map service:', error);
    }
  }

  ngOnDestroy(): void {
    window['iohMapInstance'] = null;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
